import { isMemoriesPhotoCountAllowed, MEMORY_PHOTO_LIMIT_ERROR_CODE } from "@pulse/shared";
import { supabase } from "@/lib/supabase";
import { fetchConsumerEntitlementsRpc } from "@/lib/consumerEntitlements";
import { parseMemoriesPhotoLimitFromPostgrest, MemoriesPhotoLimitError } from "@/lib/entitlementErrors";

/** Shared with dissolve photo cleanup in squad.ts */
export const MEMORY_PHOTO_PATH_RE = /memory-photos\/(.+)$/;

export async function saveSquadNightToMemories(squadNightId: string): Promise<void> {
  const { error } = await supabase
    .from("squad_nights")
    .update({ saved_to_memories: true })
    .eq("id", squadNightId);

  if (error) throw error;
}

export interface Memory {
  id: string;
  squadNightId: string | null;
  squadId?: string | null;
  createdBy: string;
  nightDate: string;
  nightName: string | null;
  venueSnapshots: Array<{
    name: string;
    category: string;
    timeFrom: string | null;
  }>;
  photoUrls: string[];
  sharedWithSquad: boolean;
  createdAt: string;
}

type MemoryRow = {
  id: string;
  squad_night_id: string | null;
  created_by: string;
  night_date: string;
  night_name: string | null;
  venue_snapshots: Memory["venueSnapshots"] | null;
  photo_urls: string[] | null;
  shared_with_squad: boolean;
  created_at: string;
  squad_nights?: { squad_id: string | null; name: string | null } | null;
};

const memories = () => supabase.from("memories");
export { memories as memoriesTable };

export async function saveMemory(
  squadNightId: string,
  nightDate: string,
  nightName: string | null,
  venueSnapshots: Array<{ name: string; category: string; timeFrom: string | null }>,
): Promise<void> {
  const {
    data: { user },
  } = await supabase.auth.getUser();
  if (!user) throw new Error("Not authenticated");

  const { data: existing } = await memories()
    .select("id")
    .eq("squad_night_id", squadNightId)
    .eq("created_by", user.id)
    .maybeSingle();

  if (existing) {
    const { error } = await memories()
      .update({
        venue_snapshots: venueSnapshots,
        night_name: nightName,
      })
      .eq("id", existing.id);
    if (error) throw error;
  } else {
    const { error } = await memories().insert({
      squad_night_id: squadNightId,
      created_by: user.id,
      night_date: nightDate,
      night_name: nightName,
      venue_snapshots: venueSnapshots,
      shared_with_squad: true,
    });
    if (error) throw error;
  }
}

export async function fetchMemories(userId: string): Promise<Memory[]> {
  const { data, error } = await memories()
    .select("*, squad_nights(squad_id, name)")
    .eq("created_by", userId)
    .order("night_date", { ascending: false });

  if (error) throw error;

  return ((data ?? []) as MemoryRow[]).map((row) => ({
    id: row.id,
    squadNightId: row.squad_night_id,
    squadId: row.squad_nights?.squad_id ?? null,
    createdBy: row.created_by,
    nightDate: row.night_date,
    nightName: row.night_name,
    venueSnapshots: row.venue_snapshots ?? [],
    photoUrls: row.photo_urls ?? [],
    sharedWithSquad: row.shared_with_squad,
    createdAt: row.created_at,
  }));
}

// Photo limits come from consumer entitlements (maxMemoriesPhotos).

async function compressImage(file: File, maxWidth = 1200, quality = 0.82): Promise<Blob> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);

    img.onload = () => {
      URL.revokeObjectURL(url);

      let { width, height } = img;
      if (width > maxWidth) {
        height = Math.round((height * maxWidth) / width);
        width = maxWidth;
      }

      const canvas = document.createElement("canvas");
      canvas.width = width;
      canvas.height = height;

      const ctx = canvas.getContext("2d");
      if (!ctx) {
        reject(new Error("Canvas not supported"));
        return;
      }

      ctx.drawImage(img, 0, 0, width, height);

      const outputType = file.type === "image/png" ? "image/png" : "image/jpeg";
      canvas.toBlob(
        (blob) => (blob ? resolve(blob) : reject(new Error("Compression failed"))),
        outputType,
        quality,
      );
    };

    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("Image load failed"));
    };
    img.src = url;
  });
}

export async function addPhotoToMemory(
  memoryId: string,
  userId: string,
  file: File,
): Promise<string> {
  const allowedTypes = ["image/jpeg", "image/png", "image/webp", "image/heic", "image/heif"];
  if (!allowedTypes.includes(file.type)) {
    throw new Error("Ungültiges Dateiformat.");
  }
  if (file.size > 10 * 1024 * 1024) {
    throw new Error("Datei zu gross. Maximum: 10MB");
  }

  const compressed = await compressImage(file);

  const ext = file.type === "image/png" ? "png" : "jpg";
  const path = `${userId}/${memoryId}/${Date.now()}.${ext}`;

  const { error: uploadError } = await supabase.storage
    .from("memory-photos")
    .upload(path, compressed, { upsert: false, contentType: compressed.type });

  if (uploadError) throw uploadError;

  const {
    data: { publicUrl },
  } = supabase.storage.from("memory-photos").getPublicUrl(path);

  const { data: existing } = await memories()
    .select("photo_urls")
    .eq("id", memoryId)
    .eq("created_by", userId)
    .single();

  const currentUrls: string[] = existing?.photo_urls ?? [];

  const resolved = await fetchConsumerEntitlementsRpc();
  if (
    resolved.entitlements &&
    !isMemoriesPhotoCountAllowed(currentUrls.length, resolved.entitlements)
  ) {
    throw new MemoriesPhotoLimitError(resolved.entitlements.maxMemoriesPhotos ?? 5);
  }

  const { error: updateError } = await memories()
    .update({ photo_urls: [...currentUrls, publicUrl] })
    .eq("id", memoryId)
    .eq("created_by", userId);

  if (updateError) {
    if (updateError.message?.includes(MEMORY_PHOTO_LIMIT_ERROR_CODE)) {
      const parsed = parseMemoriesPhotoLimitFromPostgrest(updateError);
      if (parsed) throw parsed;
    }
    throw updateError;
  }

  return publicUrl;
}

/** Remove one photo from a Circle night memory (storage + photo_urls array). */
export async function removePhotoFromMemory(
  memoryId: string,
  userId: string,
  photoUrl: string,
): Promise<void> {
  const { data: existing, error: fetchError } = await memories()
    .select("photo_urls")
    .eq("id", memoryId)
    .eq("created_by", userId)
    .single();
  if (fetchError) throw fetchError;

  const currentUrls: string[] = existing?.photo_urls ?? [];
  const nextUrls = currentUrls.filter((url) => url !== photoUrl);
  if (nextUrls.length === currentUrls.length) return;

  const path = photoUrl.match(MEMORY_PHOTO_PATH_RE)?.[1];
  if (path) {
    const { error: storageError } = await supabase.storage.from("memory-photos").remove([path]);
    if (storageError) console.error("[RemoveMemoryPhoto] Storage error:", storageError);
  }

  const { error: updateError } = await memories()
    .update({ photo_urls: nextUrls })
    .eq("id", memoryId)
    .eq("created_by", userId);
  if (updateError) throw updateError;
}

